import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Zap, Crown, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const plans = [
  {
    id: 'free',
    name: 'Free',
    description: 'Perfect for getting started',
    price: 0,
    icon: Building2,
    features: [
      '1 Business',
      '7-day history',
      '3 AI requests/day',
      'Basic analytics',
    ],
    popular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'For growing businesses',
    price: 299,
    icon: Zap,
    features: [
      'Up to 3 Businesses',
      'Unlimited history',
      '20 AI requests/day',
      'Up to 3 team members',
      'CSV & Excel exports',
      'WhatsApp summaries',
      'Expense approvals',
    ],
    popular: true,
  },
  {
    id: 'plus',
    name: 'Plus',
    description: 'For serious operators',
    price: 799,
    icon: Crown,
    features: [
      'Unlimited Businesses',
      'Unlimited AI requests',
      'Unlimited team members',
      'Loan Readiness Score',
      'Premium analytics',
      'All export formats',
      'Dedicated support',
    ],
    popular: false,
  },
];

interface PricingSectionProps {
  user: any;
}

export const PricingSection = ({ user }: PricingSectionProps) => {
  const navigate = useNavigate();

  const handleSelectPlan = (planId: string) => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    if (planId === 'free') {
      navigate('/dashboard');
    } else {
      navigate('/pricing');
    }
  };

  return (
    <section id="pricing" className="relative py-20 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 gradient-mesh opacity-30" />
      
      <div className="container relative z-10 px-4">
        <div className="text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4">Pricing</Badge>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Simple, <span className="text-gradient">Transparent</span> Pricing
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Start free, upgrade when you need more. No hidden fees, cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan) => {
            const Icon = plan.icon;
            
            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col transition-all duration-300 hover:shadow-xl ${
                  plan.popular
                    ? 'border-primary shadow-lg shadow-primary/10 scale-[1.02] md:scale-105'
                    : 'border-border hover:border-primary/30'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-4 shadow-lg">
                      Most Popular
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-2 pt-8">
                  <div className={`mx-auto mb-4 p-3 rounded-xl ${
                    plan.popular ? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    <Icon className={`h-7 w-7 ${
                      plan.popular ? 'text-primary' : 'text-muted-foreground'
                    }`} />
                  </div>
                  <CardTitle className="text-xl md:text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.description}</CardDescription>
                </CardHeader>

                <CardContent className="flex-1 px-6">
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-3xl md:text-4xl font-bold">₹{plan.price}</span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    {plan.price > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        14-day free trial included
                      </p>
                    )}
                  </div>

                  <div className="space-y-2.5">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-2.5">
                        <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>

                <CardFooter className="px-6 pb-6">
                  <Button
                    className="w-full"
                    variant={plan.popular ? 'default' : 'outline'}
                    size="lg"
                    onClick={() => handleSelectPlan(plan.id)}
                  >
                    {plan.id === 'free' ? 'Get Started' : 'Start Free Trial'}
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Enterprise CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground text-sm">
            Need a custom solution for your enterprise?{' '}
            <Button variant="link" className="p-0 h-auto text-primary">
              Contact Sales
            </Button>
          </p>
        </div>
      </div>
    </section>
  );
};
